import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import CustomerSideBar from "./CustomerSideBar"; 
import { API_URL } from "../../constant";

const CustomerLayout = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [containerStyle, setContainerStyle] = useState({});
  const [showAgentFormModal, setShowAgentFormModal] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      navigate("/sign_in");
      return;
    }

    setIsLoading(true);
    try {
      // Get user data
      const userResponse = await fetch(`${API_URL}/api/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!userResponse.ok) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user");
        throw new Error("Invalid or expired token");
      }
      
      const mainUser = await userResponse.json();
      
      // Get customer profile
      const customerResponse = await fetch(`${API_URL}/api/customer/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      let customerData = null;
      if (customerResponse.ok) {
        customerData = await customerResponse.json();
      }
      
      // Check if user is advertising agent
      let isAdvertisingAgent = false;
      let agentRole = "Customer";
      let agentType = null;
      
      try {
        const referralResponse = await fetch(`${API_URL}/api/customer/referral/info`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (referralResponse.ok) {
          const referralData = await referralResponse.json();
          isAdvertisingAgent = referralData.is_advertising_agent || false;
          
          if (isAdvertisingAgent) {
            // Get agent details
            try {
              const agentResponse = await fetch(`${API_URL}/api/customer/me`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });
              
              if (agentResponse.ok) {
                const agentData = await agentResponse.json();
                agentType = agentData?.user?.role; 
                agentRole = agentType;
              }
            } catch (error) {
              console.error("Error fetching agent details:", error);
            }
          }
        }
      } catch (error) {
        console.error("Error checking agent status:", error);
      }

      // Transform data for CustomerSideBar
      const transformedUser = {
        firstName: customerData?.user?.first_name || mainUser.user?.name?.split(' ')[0] || "Customer",
        sureName: customerData?.user?.last_name || mainUser.user?.name?.split(' ').slice(1).join(' ') || "",
        fullName: customerData?.user?.first_name ? 
          `${customerData.user.first_name} ${customerData.user.last_name}`.trim() : 
          mainUser.user?.name || "Customer",
        role: agentRole,
        avatar: customerData?.user?.photo_url || mainUser.user?.avatar || "",
        email: customerData?.user?.email || mainUser.user?.email,
        id: customerData?.user?.customer_id || mainUser.user?.id,
        isAdvertisingAgent: isAdvertisingAgent,
        agentType: agentType,
        referralCode: customerData?.user?.referral_code || "",
      };

      setUserData(transformedUser);
      
      // Set background based on role
      setContainerStyle(getContainerStyle(agentRole));
      
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError(error.message);
      navigate("/sign_in");
    } finally {
      setIsLoading(false);
    }
  };

  const getContainerStyle = (role) => {
    let background = '';
    
    switch(role) {
      case 'Senior Customer Advertising Agent':
        background = 'linear-gradient(to bottom, #0d6efd, white)';
        break;
      case 'Mini Customer Advertising Agent':
        background = 'linear-gradient(to bottom, #0000a0, white)';
        break;
      case 'Customer':
      default:
        background = 'linear-gradient(to bottom, #6f42c1, white)';
        break;
    }
    
    return {
      display: "flex",
      flexDirection: "column",
      marginTop: "0px",
      marginBottom: "0px",
      background: background,
      width: "100%",
      minHeight: "100vh",
      fontFamily: "'Poppins', sans-serif"
    };
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ marginTop: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ color: "red" }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="pagecontainer1" style={containerStyle}>
      <CustomerSideBar 
        user={userData} 
        onAgentRequest={() => setShowAgentFormModal(true)} 
      />
      <Outlet context={{ 
        userData, 
        fetchUserData,
        showAgentFormModal,
        setShowAgentFormModal 
      }} />
    </div>
  );
};

export default CustomerLayout;